import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function BlogPage() {
  const featuredPost = {
    title: 'The Future of Live Events: What to Expect in 2026',
    excerpt: 'From AI-powered recommendations to seamless mobile entry, discover how technology is transforming the live event experience.',
    date: 'January 20, 2026',
    category: 'Industry Trends',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=800&h=400&fit=crop'
  };

  const posts = [
    {
      title: 'How to Score the Best Seats: A Buyer\'s Guide',
      excerpt: 'Expert tips for finding premium seats at face value or below.',
      date: 'January 18, 2026',
      category: 'Tips & Guides',
      readTime: '4 min read'
    },
    {
      title: 'Introducing Our New Seller Dashboard',
      excerpt: 'Manage your listings, track sales, and get paid faster with our redesigned seller experience.',
      date: 'January 15, 2026',
      category: 'Product Updates',
      readTime: '3 min read'
    },
    {
      title: 'Top 10 Concerts Coming This Spring',
      excerpt: 'From stadium tours to intimate venues, here are the shows you won\'t want to miss.',
      date: 'January 12, 2026',
      category: 'Event Highlights',
      readTime: '6 min read'
    },
    {
      title: 'Understanding Dynamic Pricing in Ticketing',
      excerpt: 'Why ticket prices fluctuate and how to time your purchase for the best deal.',
      date: 'January 10, 2026',
      category: 'Industry Trends',
      readTime: '5 min read'
    },
    {
      title: 'Our Commitment to Ticket Authenticity',
      excerpt: 'How BESTIX verifies every ticket to protect buyers from fraud.',
      date: 'January 8, 2026',
      category: 'Trust & Safety',
      readTime: '4 min read'
    },
    {
      title: 'Mobile Tickets: Everything You Need to Know',
      excerpt: 'A complete guide to receiving, storing, and using digital tickets.',
      date: 'January 5, 2026',
      category: 'Tips & Guides',
      readTime: '3 min read'
    }
  ];

  const categories = ['All', 'Industry Trends', 'Tips & Guides', 'Product Updates', 'Event Highlights', 'Trust & Safety'];

  return (
    <>
      <Helmet><title>Blog | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Blog</h1>
            <p className="mt-4 text-xl text-gray-300">News, tips, and insights from BESTIX</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-12 justify-center">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  index === 0
                    ? 'bg-navy text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Featured Post */}
          <div className="mb-12">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-gold/10 text-gold text-xs font-semibold px-3 py-1 rounded-full">
                      {featuredPost.category}
                    </span>
                    <span className="text-gray-400 text-sm">{featuredPost.readTime}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-600 mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{featuredPost.date}</span>
                    <button className="text-navy font-semibold hover:text-gold transition-colors">
                      Read More →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Post Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <article key={index} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-40 bg-gradient-to-br from-navy to-navy-dark flex items-center justify-center">
                  <svg className="w-12 h-12 text-gold/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs text-gold font-medium">{post.category}</span>
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-400">{post.readTime}</span>
                  </div>
                  <h3 className="font-semibold text-navy mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{post.date}</span>
                    <button className="text-sm text-navy font-medium hover:text-gold transition-colors">
                      Read →
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Newsletter CTA */}
          <div className="mt-16">
            <div className="bg-navy rounded-2xl p-8 md:p-12 text-center">
              <h3 className="text-2xl font-playfair font-bold text-white mb-4">
                Stay in the Loop
              </h3>
              <p className="text-gray-300 mb-6 max-w-xl mx-auto">
                Get the latest event news, exclusive presale access, and insider tips delivered to your inbox.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg focus:ring-2 focus:ring-gold focus:outline-none"
                />
                <button type="submit" className="btn btn-primary whitespace-nowrap">
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
